import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { StandardRefinement } from '../Refinements/StandardRefinement';
import { NumericRangeRefinement } from '../Refinements/CustomNumericRange/NumericRangeRefinement';
import { sortRefinements, isRefinementActive, extractNavParamFromClearAllURL } from '../../product-results-helpers';
import { ProductResultsContext } from '../ProductResultsContext';

const NumericRangeDimension = ({
  dimension,
  onChange,
  onDisplayData,
  enableMultiStore,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  fitCompatibility,
  metadata,
  selectedRefinements
}) => {

  const { data } = useContext(ProductResultsContext);
  const navParam = extractNavParamFromClearAllURL(data);

  const {
    refinements = []
  } = dimension || {};

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return `${refinementKey}-${refinementIndex}`;
  };

  const handleOnChange = ({
    refinement,
    dimension: dimensionCustomRefinement,
    refinementRange
  }) => {
    if (onChange) {
      if (refinementRange) {
        onChange({
          multiRefinements: refinementRange,
          dimension: { ...dimension, dimensionId: 'custom_refinement' }
        });
      } else {
        onChange({
          refinement,
          dimension: dimensionCustomRefinement
        });
      }
    }
    return null;
  };

  const sortedRefinements = refinements ? [...refinements].sort(sortRefinements) : null;

  const wrapperClassNames = classNames(
    'sui-max-h-[275px]',
    'sui-overflow-y-auto'
  );

  const containerClassNames = classNames(
    'sui-flex',
    'sui-flex-wrap',
    'sui-flex-col',
    'sui-gap-2',
    'sui-mb-1',
    'multi-select-refinement-secondary-drawer'
  );

  return (
    <>
      {fitCompatibility && (
        <div
          role="button"
          tabIndex={0}
          aria-label="fit-compatibility"
          className="sui-mb-3"
        >
          <FitCompatibility
            type="MINI"
            drawerPosition="left"
            imageCacheEnabled
            navParam={navParam}
            data={data}
          />
        </div>
      )}
      <NumericRangeRefinement
        dimension={dimension}
        metadata={metadata}
        onChange={handleOnChange}
      />
      <section className={wrapperClassNames}>
        <div
          className={containerClassNames}
          data-testid="refinement-secondary-drawer"
          data-instoredisplay="true"
        >
          {
            sortedRefinements && (
              sortedRefinements
                .map((refinement, refinementIndex) => {
                  const id = getRefinementId(refinement, refinementIndex);
                  const isRefinementSelected = isRefinementActive(refinement.refinementKey, selectedRefinements);

                  return (
                    <StandardRefinement
                      dimension={dimension}
                      id={id}
                      key={id}
                      onChange={handleOnChange}
                      enableMultiStore={enableMultiStore}
                      refinement={refinement}
                      onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                      multiStoreSelection={multiStoreSelection}
                      onDisplay={onDisplayData.onDisplay}
                      isRefinementSelected={isRefinementSelected}
                    />
                  );
                })
            )
          }
        </div>
      </section>
    </>
  );
};

NumericRangeDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  fitCompatibility: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMultiStoreSelectionChange: PropTypes.func,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    itemCount: PropTypes.string
  }),
  metadata: PropTypes.shape({
    canonicalUrl: PropTypes.string
  }).isRequired,
  selectedRefinements: PropTypes.arrayOf(PropTypes.shape({}))
};

NumericRangeDimension.defaultProps = {
  onDisplayData: {
    onDisplay: false,
    onDisplayChange: null,
    isStoreDisplay: 'false',
    itemCount: null
  },
  fitCompatibility: false,
  enableMultiStore: false,
  onMultiStoreSelectionChange: null,
  multiStoreSelection: null,
  selectedRefinements: null
};

NumericRangeDimension.displayName = 'NumericRangeDimension';

export { NumericRangeDimension };
